<template>
  <div
    class="safe-insets h-viewport min-h-viewport relative flex w-full flex-col overflow-hidden bg-black"
  >
    <div v-if="phase === 'idle' && !isOverrideActive" class="h-full min-h-0 w-full bg-hazel-400">
      <DeviceIdleScreen />
    </div>
    <CounterVideoWallPlayer
      v-else-if="displayedVideo"
      :key="`${displayedVideo.kind}:${displayedVideo.fileUrl}:${playbackKey}`"
      :file-url="displayedVideo.fileUrl"
      :poster-file-url="displayedVideo.posterFileUrl"
      @playing="handleVideoPlaying"
      @ended="handleVideoEnded"
      @error="handlePlaybackError"
    />

    <!-- Dip-to-black overlay: fades the current frame to black, then reveals the swapped content. -->
    <div
      class="pointer-events-none absolute inset-0 z-30 bg-black"
      :style="{ opacity: isBlackVisible ? 1 : 0, transition: `opacity ${blackDurationMs}ms ease` }"
    />
  </div>
</template>

<script setup lang="ts">
import {
  type CounterVideoWallHandoverEventDto,
  type CounterVideoWallHandoverVideoDto,
  type RotationMediaPoolItemDto,
} from '@carvia/ros-client-types';
import { DeviceAuthRole, type JwtDeviceDto } from '@carvia/ros-client-types';
import { useRotationDeck } from '@/hooks/use-rotation-deck';
import { useAutoReloadOnAppUpdate } from '@/hooks/use-auto-reload-on-app-update';
import { useMediaRotationCurrent } from '@/queries/use-media-rotation-current';
import { useStationHandoverEvents } from '@/queries/use-station-handover-events';
import { useTokenPayload } from '@/queries/auth/use-login';
import type { RouterMeta } from '@/router';
import { prefetchHlsVideo } from '@/utils/hls-video-cache';
import * as Sentry from '@sentry/vue';
import { isEqual, pick, sortBy } from 'lodash';
import { computed, nextTick, ref, toRef, watch } from 'vue';
import { useRoute } from 'vue-router';
import DeviceIdleScreen from './DeviceIdleScreen.vue';
import CounterVideoWallPlayer from './counter-video-wall/CounterVideoWallPlayer.vue';

type Phase = 'idle' | 'playing';

type DisplayVideo = {
  fileUrl: string;
  posterFileUrl?: string | null;
  kind: 'rotation' | 'handover';
  mediaId?: string;
};

const props = defineProps<{
  stationSlug?: string;
  stationAuthCode?: string;
}>();

const stationSlug = toRef(props, 'stationSlug');
const stationAuthCode = toRef(props, 'stationAuthCode');
const route = useRoute();
const { data: tokenPayload } = useTokenPayload(() => !(route.meta as RouterMeta).public);

const isVideoWallDevice = computed(() => {
  if (stationSlug.value) return false;
  const payload = tokenPayload.value;
  if (!payload || !('deviceRole' in payload)) return false;
  return (payload as JwtDeviceDto).deviceRole === DeviceAuthRole.COUNTER_VIDEO_WALL;
});

const isActive = computed(
  () => isVideoWallDevice.value || (!!stationSlug.value && !!stationAuthCode.value),
);

const stationId = computed(() => {
  if (stationSlug.value) return undefined;
  const payload = tokenPayload.value;
  if (!payload || !('deviceRole' in payload)) return undefined;
  return (payload as JwtDeviceDto).stationId;
});

const stationTag = computed(() => stationId.value ?? stationSlug.value);

const { data: poolData } = useMediaRotationCurrent(isActive, stationSlug, stationAuthCode);
const pool = computed(() => poolData.value ?? []);
const {
  rebuildDeck,
  scheduleRebuildDeck,
  consumePendingRebuild,
  syncDeckFromPool,
  peekNext,
  takeNext,
} = useRotationDeck(pool);

useAutoReloadOnAppUpdate(isActive);

const phase = ref<Phase>('idle');
const playbackKey = ref(0);
const currentItem = ref<RotationMediaPoolItemDto | null>(null);
const lastPlayedMediaId = ref<string | null>(null);
const failedMediaIds = ref<Set<string>>(new Set());
const handlingErrorMediaId = ref<string | null>(null);

const handoverQueue = ref<CounterVideoWallHandoverVideoDto[]>([]);
const seenSessionIds = ref<Set<string>>(new Set());
const overrideItem = ref<CounterVideoWallHandoverVideoDto | null>(null);
const displayedVideo = ref<DisplayVideo | null>(null);
const isOverrideActive = computed(
  () => overrideItem.value !== null || displayedVideo.value?.kind === 'handover',
);

// Dip-to-black transition state: fade to black over 500ms, swap content, then back over 300ms.
const isBlackVisible = ref(false);
const blackDurationMs = ref(500);
const FADE_TO_BLACK_MS = 500;
const FADE_FROM_BLACK_MS = 300;
let isTransitioning = false;
let transitionPending = false;

function toRotationVideo(item: RotationMediaPoolItemDto): DisplayVideo {
  return {
    fileUrl: item.fileUrl,
    posterFileUrl: item.posterFileUrl,
    kind: 'rotation',
    mediaId: item.mediaId,
  };
}

function toHandoverVideo(item: CounterVideoWallHandoverVideoDto): DisplayVideo {
  return {
    fileUrl: item.fileUrl,
    posterFileUrl: item.posterFileUrl,
    kind: 'handover',
    mediaId: item.mediaId,
  };
}

function getTargetVideo(): DisplayVideo | null {
  if (overrideItem.value) return toHandoverVideo(overrideItem.value);
  if (currentItem.value && phase.value === 'playing') return toRotationVideo(currentItem.value);
  return null;
}

function videosEqual(a: DisplayVideo | null, b: DisplayVideo | null): boolean {
  if (!a || !b) return a === b;
  return a.fileUrl === b.fileUrl;
}

function wait(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function runOverrideTransitions() {
  if (isTransitioning) {
    transitionPending = true;
    return;
  }
  isTransitioning = true;
  try {
    do {
      transitionPending = false;
      while (!videosEqual(displayedVideo.value, getTargetVideo())) {
        blackDurationMs.value = FADE_TO_BLACK_MS;
        isBlackVisible.value = true;
        await wait(FADE_TO_BLACK_MS);

        displayedVideo.value = getTargetVideo();
        await nextTick();

        blackDurationMs.value = FADE_FROM_BLACK_MS;
        isBlackVisible.value = false;
        await wait(FADE_FROM_BLACK_MS);
      }
    } while (transitionPending || !videosEqual(displayedVideo.value, getTargetVideo()));
  } finally {
    isTransitioning = false;
    if (transitionPending) {
      void runOverrideTransitions();
    }
  }
}

watch(overrideItem, () => {
  void runOverrideTransitions();
});

function setDisplayedRotation(item: RotationMediaPoolItemDto) {
  if (overrideItem.value) return;
  displayedVideo.value = toRotationVideo(item);
}

function getDeckOptions(overrides: { allowRepeat?: boolean; avoidMediaId?: string | null } = {}) {
  return {
    failedMediaIds: [...failedMediaIds.value],
    avoidMediaId: overrides.avoidMediaId ?? lastPlayedMediaId.value,
    allowRepeat: overrides.allowRepeat,
  };
}

/** Caches the video the rotation will most likely play next, so it starts without delay. */
function prefetchNextItem() {
  if (!currentItem.value) return;

  const next = peekNext(getDeckOptions({ avoidMediaId: currentItem.value.mediaId }));
  if (next) {
    void prefetchHlsVideo(next.fileUrl);
  }
}

function advancePlayback(item: RotationMediaPoolItemDto) {
  currentItem.value = item;
  phase.value = 'playing';
  setDisplayedRotation(item);
}

function startPlaybackIfNeeded() {
  if (currentItem.value || !pool.value.length) {
    if (!pool.value.length && !currentItem.value) {
      phase.value = 'idle';
    }
    return;
  }

  const picked = takeNext(getDeckOptions());
  if (!picked) {
    phase.value = 'idle';
    return;
  }

  advancePlayback(picked);
}

const previousPool = ref<RotationMediaPoolItemDto[]>([]);

watch(
  pool,
  (newPool) => {
    const poolStructure = (items: RotationMediaPoolItemDto[]) =>
      sortBy(
        items.map((item) => pick(item, ['mediaId', 'weight'])),
        'mediaId',
      );

    const structureUnchanged = isEqual(poolStructure(previousPool.value), poolStructure(newPool));

    previousPool.value = newPool;

    if (structureUnchanged) {
      syncDeckFromPool();
    } else if (currentItem.value) {
      failedMediaIds.value = new Set();
      scheduleRebuildDeck();
    } else {
      failedMediaIds.value = new Set();
      rebuildDeck();
    }

    startPlaybackIfNeeded();
  },
  { immediate: true },
);

// Prefetch only once the current video plays, so it does not compete for bandwidth while starting.
function handleVideoPlaying() {
  prefetchNextItem();
}

function handleEnded() {
  if (!currentItem.value) return;

  lastPlayedMediaId.value = currentItem.value.mediaId;

  const picked = takeNext(getDeckOptions()) ?? takeNext(getDeckOptions({ allowRepeat: true }));
  consumePendingRebuild();

  if (!picked) {
    Sentry.captureException(new Error('Counter video wall: no playable media in pool'), {
      tags: { feature: 'counter-video-wall', stationId: stationTag.value },
    });
    currentItem.value = null;
    phase.value = 'idle';
    if (!overrideItem.value) {
      displayedVideo.value = null;
    }
    return;
  }

  currentItem.value = picked;
  handlingErrorMediaId.value = null;
  playbackKey.value += 1;
  setDisplayedRotation(picked);
}

function handleVideoEnded() {
  if (displayedVideo.value?.kind === 'handover') {
    handleOverrideEnded();
    return;
  }
  handleEnded();
}

function resolveFailedItem(fileUrl: string): RotationMediaPoolItemDto | null {
  if (currentItem.value?.fileUrl === fileUrl) return currentItem.value;
  return pool.value.find((item) => item.fileUrl === fileUrl) ?? null;
}

function handlePlaybackError(payload: { fileUrl: string }) {
  if (displayedVideo.value?.kind === 'handover') {
    handleOverrideError(payload);
    return;
  }

  const failedItem = resolveFailedItem(payload.fileUrl);
  if (!failedItem) return;

  if (handlingErrorMediaId.value === failedItem.mediaId) return;
  handlingErrorMediaId.value = failedItem.mediaId;

  Sentry.captureException(new Error('Counter video wall: playback failed'), {
    tags: {
      feature: 'counter-video-wall',
      stationId: stationTag.value,
      mediaId: failedItem.mediaId,
    },
    extra: { fileUrl: payload.fileUrl },
  });

  failedMediaIds.value = new Set([...failedMediaIds.value, failedItem.mediaId]);
  lastPlayedMediaId.value = failedItem.mediaId;
  rebuildDeck([...failedMediaIds.value]);

  const next = takeNext(getDeckOptions()) ?? takeNext(getDeckOptions({ allowRepeat: true }));

  if (!next) {
    Sentry.captureException(new Error('Counter video wall: all media failed'), {
      tags: { feature: 'counter-video-wall', stationId: stationTag.value },
    });
    currentItem.value = null;
    phase.value = 'idle';
    if (!overrideItem.value) {
      displayedVideo.value = null;
    }
    return;
  }

  currentItem.value = next;
  playbackKey.value += 1;
  setDisplayedRotation(next);
}

/**
 * Pulls the next queued handover video into the override target. Returns false (and clears the
 * override) when the queue is empty, which dips to black and resumes the rotation.
 */
function startNextOverride(): boolean {
  const [next, ...rest] = handoverQueue.value;
  if (!next) {
    overrideItem.value = null;
    return false;
  }
  if (next?.mediaId === overrideItem.value?.mediaId) {
    handoverQueue.value = rest;
    return startNextOverride();
  }
  handoverQueue.value = rest;
  overrideItem.value = next;
  return true;
}

function handleHandoverEvent(event: CounterVideoWallHandoverEventDto) {
  if (event.type !== 'handover.started' || !event.video) return;
  if (seenSessionIds.value.has(event.sessionId)) return;
  seenSessionIds.value = new Set([...seenSessionIds.value, event.sessionId]);
  handoverQueue.value = [...handoverQueue.value, event.video];

  if (!overrideItem.value) {
    startNextOverride();
  }
}

function handleOverrideEnded() {
  startNextOverride();
}

function handleOverrideError(payload: { fileUrl: string }) {
  Sentry.captureException(new Error('Counter video wall: handover video playback failed'), {
    tags: {
      feature: 'counter-video-wall',
      stationId: stationTag.value,
      mediaId: overrideItem.value?.mediaId,
    },
    extra: { fileUrl: payload.fileUrl },
  });

  startNextOverride();
}

useStationHandoverEvents(isActive, handleHandoverEvent, stationSlug, stationAuthCode);
</script>
